import type { GroupLeadershipLevel } from "@/main/common/GroupGovernanceApi";
import type { DiscussionWithoutContentDTO } from "@/generated-client";

export type GroupRuleLike = {
    id?: number;
    title?: string;
    description?: string | null;
    sortOrder?: number | null;
};

export type GroupResourceLike = {
    id?: number;
    title?: string;
    description?: string | null;
    url?: string | null;
    sortOrder?: number | null;
};

export type RelatedGroupLike = {
    id?: number;
    relatedGroupPublicId?: string;
    relatedGroupName?: string;
    relatedGroupSlug?: string;
    relatedGroupThumbnailUrl?: string | null;
    relatedGroupDescription?: string | null;
    sortOrder?: number | null;
};

export type GroupLeaderLike = {
    username?: string;
    profileImageUrl?: string;
    leadershipLevel?: GroupLeadershipLevel;
};

export type PinnedGroupDiscussionLike = DiscussionWithoutContentDTO & {
    id?: number;
    pinId?: number;
    sortOrder?: number | null;
    pinnedAt?: string | null;
    pinnedByUsername?: string | null;
};

/**
 * Sizes of the four manage-console queues, for a leader's pending-work badges.
 *
 * <p><b>The whole record is absent for anyone who is not a leader of this group</b>, and that is the
 * contract rather than a convenience: the governance bootstrap is built for every accessible viewer,
 * so "how many people are waiting to get in" and "how many members are banned" must not travel to
 * someone who cannot act on either. The backend gates it in SQL; nothing here has to re-decide it.
 *
 * <p>Absent is also the correct shape on the AJAX fallback path, which has no bootstrap at all — no
 * counts means no badges, which is the honest rendering of "we do not know yet". Never zero-fill it:
 * a zero is indistinguishable from an empty queue.
 */
export type GroupLeaderQueueCounts = {
    joinRequests: number;
    discussionSubmissions: number;
    invitations: number;
    bannedMembers: number;
    /**
     * The fifth queue (2026-09-02): members asking a leader to invite somebody into a private group.
     *
     * <p>Gated in SQL by `group_leaders` membership, like the other four. That gate matches the service
     * bar exactly — every leader row is MODERATOR or above, and resolving a request needs MODERATOR+ —
     * so the set that sees this badge is the set that can clear it.
     */
    inviteRequests?: number;
};

export type GroupGovernanceBootstrap = {
    rules?: GroupRuleLike[];
    resources?: GroupResourceLike[];
    relatedGroups?: RelatedGroupLike[];
    leaders?: GroupLeaderLike[];
    pinnedDiscussions?: PinnedGroupDiscussionLike[];
    pinnedDiscussionsLimit?: number | null;
    /**
     * The owner's new-joiner greeting. Ships here rather than as a group `data-*` attribute so it
     * arrives alongside the rules and resources the welcome dialog renders beside it — and so it never
     * has to travel through the column-explicit `group_base_view` that also feeds the directory,
     * search and recommendations. See
     * designs/01-now/2026-07-21-group-new-joiner-welcome-technical-design.md §2.
     */
    welcomeMessage?: string | null;
    /** The group's tag names, rendered as the welcome dialog's Topics pills. */
    topics?: string[];
    /** See {@link GroupLeaderQueueCounts}. Null/absent for every viewer who is not a leader. */
    leaderQueueCounts?: GroupLeaderQueueCounts | null;
};

/**
 * Reads the four counts off an untrusted bootstrap payload, or returns null.
 *
 * <p>All-or-nothing on purpose: a payload missing one of the four is a shape this client does not
 * understand, and rendering three real badges beside one silently-zeroed one would be worse than
 * rendering none.
 */
export function normalizeLeaderQueueCounts(raw: unknown): GroupLeaderQueueCounts | null {
    if (!raw || typeof raw !== "object") {
        return null;
    }

    const candidate = raw as Partial<GroupLeaderQueueCounts>;
    const readCount = (value: unknown): number | null => (
        typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) : null
    );

    const joinRequests = readCount(candidate.joinRequests);
    const discussionSubmissions = readCount(candidate.discussionSubmissions);
    const invitations = readCount(candidate.invitations);
    const bannedMembers = readCount(candidate.bannedMembers);
    // ⚠️ The fifth count is read LENIENTLY, and the asymmetry with the four above is deliberate.
    // Requiring it would mean a bootstrap serialized before 2026-09-02 — a page cached across the
    // deploy, or a bfcache restore — fails the guard below and nulls the WHOLE record, so a member
    // would lose all four existing badges to gain one. Rule 34's lesson restated: make the failure
    // direction a missing link, never a broken one. Absent stays absent (never zero-filled), so the
    // fifth badge simply does not render until a fresh bootstrap carries it.
    const inviteRequests = readCount(candidate.inviteRequests);

    if (joinRequests === null || discussionSubmissions === null || invitations === null || bannedMembers === null) {
        return null;
    }

    return inviteRequests === null
        ? { joinRequests, discussionSubmissions, invitations, bannedMembers }
        : { joinRequests, discussionSubmissions, invitations, bannedMembers, inviteRequests };
}

export function orderPinnedDiscussions<T extends { sortOrder?: number | null; pinId?: number | null }>(items: T[]): T[] {
    return [...items].sort((left, right) => {
        const leftSortOrder = left.sortOrder ?? Number.MAX_SAFE_INTEGER;
        const rightSortOrder = right.sortOrder ?? Number.MAX_SAFE_INTEGER;

        if (leftSortOrder !== rightSortOrder) {
            return leftSortOrder - rightSortOrder;
        }

        const leftPinId = left.pinId ?? Number.MAX_SAFE_INTEGER;
        const rightPinId = right.pinId ?? Number.MAX_SAFE_INTEGER;
        return leftPinId - rightPinId;
    });
}

export function normalizePinnedDiscussions<T extends PinnedGroupDiscussionLike>(items: T[]): T[] {
    return orderPinnedDiscussions(items).map((item, index) => ({
        ...item,
        id: item.pinId ?? item.id,
        sortOrder: item.sortOrder ?? index,
    }));
}

export type GroupRailSectionUpdate =
    | { kind: "rule"; items: GroupRuleLike[] }
    | { kind: "resource"; items: GroupResourceLike[] }
    | { kind: "related"; items: RelatedGroupLike[] };
